import { ADMIN_PERMISSION_CATALOG, type AdminPermissionMeta, isGranted } from "./adminPermissionCatalog";
import type { AdminPermission, AdminRole, AdminRoleDefaults, AdminUser } from "./types";

/**
 * The role rules. The frontend decides what a role does to the checkboxes, so the admin sees the
 * effect before saving. Pure functions only, so they test directly.
 */

/**
 * The roles, mirroring the backend `admin_role` enum. The `Record` check means forgetting one
 * is a type error, not a role quietly missing from the picker.
 */
const ADMIN_ROLE_PRESENCE = {
	Scheduler: true,
	Admin: true,
	Supervisor: true,
	HR: true,
	Viewer: true,
	NursingLeadership: true,
	Network: true,
} as const satisfies Record<AdminRole, true>;

export const ADMIN_ROLES = Object.keys(ADMIN_ROLE_PRESENCE) as readonly AdminRole[];

/** The three permissions where `true` removes power. All others: `true` = can do. */
export const RESTRICTION_PERMISSIONS: readonly AdminPermission[] = ["is_readonly", "hide_wage", "disable_edit_master_schedule"] as const;

/** Backend permission name → the form field it sets. Anything not listed here is ignored. */
export const PERMISSION_TO_FIELD = {
	is_readonly: "isReadonly",
	hide_wage: "hideWage",
	disable_edit_master_schedule: "disableEditMasterSchedule",
	modify_user: "modifyUser",
	call_off_admin: "callOffAdmin",
	receive_daily_admin_notifications: "receiveDailyAdminNotifications",
	receive_action_required_admin_notifications: "receiveActionRequiredAdminNotifications",
	send_daily_pickup_summary: "sendDailyPickupSummary",
	receive_performance_report: "receivePerformanceReport",
	receive_daily_excel_report: "receiveDailyExcelReport",
	receive_sms_response_notifications: "receiveSmsResponseNotifications",
	receive_nurse_inactivity_notifications: "receiveNurseInactivityNotifications",
} as const satisfies Record<AdminPermission, keyof AdminUser>;

const isRestriction = (permission: AdminPermission): boolean => RESTRICTION_PERMISSIONS.includes(permission);

const isKnownPermission = (permission: string): permission is AdminPermission => permission in PERMISSION_TO_FIELD;

/** What one role grants, ignoring names the UI does not know. */
const grantsOf = (role: AdminRole, defaults: AdminRoleDefaults): AdminPermission[] => (defaults[role] ?? []).filter(isKnownPermission);

/** What all these roles grant, combined. */
export const grantsOfAll = (roles: readonly AdminRole[], defaults: AdminRoleDefaults): Set<AdminPermission> => {
	const granted = new Set<AdminPermission>();
	for (const role of roles) {
		for (const permission of grantsOf(role, defaults)) granted.add(permission);
	}
	return granted;
};

/**
 * Work out a row's checkboxes when the roles change. Returns a new row; never mutates the input.
 *
 * - **Normal permissions** — an added role turns its permissions on. A removed role turns them off,
 *   unless a role still held grants them. Anything no changed role granted is left alone.
 * - **The three restriction flags** — the current roles decide both ways: on if any grants it, off
 *   if none does (more access wins). So `hideWage` stays on for Scheduler but clears for Admin.
 */
export const applyRoleChange = ({
	row,
	previousRoles,
	nextRoles,
	defaults,
}: {
	row: AdminUser;
	previousRoles: readonly AdminRole[];
	nextRoles: readonly AdminRole[];
	defaults: AdminRoleDefaults;
}): AdminUser => {
	const nextRoleSet = new Set(nextRoles);
	const previousRoleSet = new Set(previousRoles);

	const addedRoles = nextRoles.filter((role) => !previousRoleSet.has(role));
	const removedRoles = previousRoles.filter((role) => !nextRoleSet.has(role));

	const grantedByNext = grantsOfAll(nextRoles, defaults);
	const grantedByAdded = grantsOfAll(addedRoles, defaults);
	const grantedByRemoved = grantsOfAll(removedRoles, defaults);

	const updated: AdminUser = { ...row, adminRoles: [...nextRoles] };

	for (const permission of Object.keys(PERMISSION_TO_FIELD) as AdminPermission[]) {
		const field = PERMISSION_TO_FIELD[permission];

		if (isRestriction(permission)) {
			// More access wins: the restriction applies only while EVERY selected role asks for it, so
			// one role that does not lifts it. `every` on an empty list is true, so clearing all roles
			// would hold the restriction -- but no role means no restriction, so that case clears too.
			updated[field] = nextRoles.length > 0 && nextRoles.every((role) => grantsOf(role, defaults).includes(permission));
			continue;
		}

		// Only a role that changed can move a normal permission.
		if (grantedByAdded.has(permission)) {
			updated[field] = true;
		} else if (grantedByRemoved.has(permission) && !grantedByNext.has(permission)) {
			updated[field] = false;
		}
	}

	return updated;
};

/**
 * The permissions this row holds that its roles do not grant — or lacks that they do.
 *
 * Derived, not stored: a permission is customized only while it disagrees with the roles, so
 * editing the defaults in SQL re-labels every affected row on the next load.
 *
 * With no role, a normal switch that is on or a flipped switch that is off reads as customized.
 */
export const customizedPermissions = (row: AdminUser, defaults: AdminRoleDefaults): AdminPermission[] =>
	// `roleDefaultGrant`, not a raw union: a restriction needs EVERY role to ask for it, so
	// Scheduler+Admin clears `hide_wage`. The union reported a customization on untouched rows.
	ADMIN_PERMISSION_CATALOG.filter((meta) => isGranted(row, meta) !== roleDefaultGrant(row.adminRoles, meta, defaults)).map((meta) => meta.permission);

/** Do two role lists differ, ignoring order? Decides whether a row needs a roles save. */
export const haveRolesChanged = (a: readonly AdminRole[] | undefined, b: readonly AdminRole[] | undefined): boolean => {
	const left = [...(a ?? [])].sort();
	const right = [...(b ?? [])].sort();
	if (left.length !== right.length) return true;
	return left.some((role, i) => role !== right[i]);
};

/** Which of this user's roles grant this permission. */
export const rolesGranting = (roles: readonly AdminRole[], permission: AdminPermission, defaults: AdminRoleDefaults): AdminRole[] =>
	roles.filter((role) => grantsOf(role, defaults).includes(permission));

/** The roles that keep this switch on. For an inverted switch, those are the roles without the restriction. */
const rolesTurningOn = (roles: readonly AdminRole[], meta: AdminPermissionMeta, defaults: AdminRoleDefaults): AdminRole[] => {
	const granting = rolesGranting(roles, meta.permission, defaults);
	return meta.inverted ? roles.filter((role) => !granting.includes(role)) : granting;
};

/**
 * Where a switch's value came from, for the panel's line under each switch. In displayed terms.
 *
 * - `fromRoles` — matches the roles; these are the ones turning it on.
 * - `onWithoutRole` — an inverted switch that is on because the person has no role.
 * - `custom` — disagrees with them. `addedForPerson` says which way, `wouldGrant` names the roles.
 */
export type PermissionSource =
	| { kind: "fromRoles"; roles: AdminRole[] }
	| { kind: "onWithoutRole" }
	| { kind: "notGranted"; hasAnyRole: boolean }
	| { kind: "custom"; addedForPerson: boolean; wouldGrant: AdminRole[] };

export const permissionSource = (row: AdminUser, meta: AdminPermissionMeta, defaults: AdminRoleDefaults): PermissionSource => {
	const turningOn = rolesTurningOn(row.adminRoles, meta, defaults);
	const ticked = isGranted(row, meta);
	const roleValue = roleDefaultGrant(row.adminRoles, meta, defaults);
	const hasAnyRole = row.adminRoles.length > 0;

	if (ticked !== roleValue) {
		return { kind: "custom", addedForPerson: ticked, wouldGrant: turningOn };
	}
	if (ticked) return hasAnyRole ? { kind: "fromRoles", roles: turningOn } : { kind: "onWithoutRole" };
	return { kind: "notGranted", hasAnyRole };
};

/** Stored rule for a restriction: on only while every role asks for it, and no role means off. */
const restrictionByRoles = (roles: readonly AdminRole[], permission: AdminPermission, defaults: AdminRoleDefaults): boolean =>
	roles.length > 0 && rolesGranting(roles, permission, defaults).length === roles.length;

/**
 * What the roles alone would show on the switch — what "use role default" restores. In displayed
 * terms, so it compares directly with `isGranted`.
 *
 * Mirrors `applyRoleChange`: any role grants a normal permission, but a restriction needs every
 * role to ask for it, and no role means no restriction. Drift here = panel disagreeing with grid.
 */
export const roleDefaultGrant = (roles: readonly AdminRole[], meta: AdminPermissionMeta, defaults: AdminRoleDefaults): boolean => {
	const stored = isRestriction(meta.permission)
		? restrictionByRoles(roles, meta.permission, defaults)
		: rolesGranting(roles, meta.permission, defaults).length > 0;
	return meta.inverted ? !stored : stored;
};
